import React, { useEffect, useState } from "react";
import {
  Spin,
  InputNumber,
  Button,
  message,
  Popconfirm,
  Modal,
  Input,
  Empty,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Cart.css";

const FALLBACK_IMG = "https://via.placeholder.com/100";

const Cart = ({ refreshCartCount }) => {
  const navigate = useNavigate();
  const [cartData, setCartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [detailModal, setDetailModal] = useState({
    open: false,
    item: null,
    value: "",
  });

  useEffect(() => {
    checkUserActiveStatus();
    fetchCartData();
    // eslint-disable-next-line
  }, []);

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          // Redirect to home if AllowtoOrder is false
          if (response.data.AllowtoOrder === false) {
            window.location.href = "/";
            return;
          }

          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === true
          ) {
            alert("Your session has expired. Please log in again.");

            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            window.location.reload();
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const fetchCartData = async () => {
    setLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.get(
        global.config.APIURL.BaseURL + "api/UserCart/GetCartData",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      setCartData(response.data || []);
    } catch (error) {
      setCartData([]);
    }
    setLoading(false);
  };

  const handleQtyChange = async (item, newNoOfBox) => {
    if (newNoOfBox < 1) return;
    setUpdatingId(item.ID);

    // Calculate new dependent values
    const NoOfBox = newNoOfBox;
    const BoxSize = item.BoxSize;
    const TotalQuantity = NoOfBox * BoxSize;
    const Price = item.Price;
    const Amount = Price * TotalQuantity;

    // Prepare payload (all other data remains same)
    const payload = {
      ID: item.ID,
      UserID: item.UserID,
      ProductID: item.ProductID,
      Color: item.Color,
      Size: item.Size,
      NoOfBox,
      BoxSize,
      TotalQuantity,
      Price,
      Amount,
      Detail: item.Detail,
      AddedOn: item.AddedOn,
      LastUpdatedOn: new Date(),
    };

    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/UserCart/UpdateQtyInCart",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Quantity updated!");
        setCartData((prev) =>
          prev.map((cartItem) =>
            cartItem.ID === item.ID
              ? {
                  ...cartItem,
                  NoOfBox,
                  TotalQuantity,
                  Amount,
                }
              : cartItem
          )
        );
      } else {
        message.error("Failed to update quantity.");
      }
    } catch (error) {
      message.error("Failed to update quantity.");
    }
    setUpdatingId(null);
  };

  const handleRemove = async (cartId) => {
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.delete(
        global.config.APIURL.BaseURL +
          `api/UserCart/DeleteFromCart?CartID=${cartId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Removed from cart!");
        setCartData((prev) => prev.filter((item) => item.ID !== cartId));
        if (refreshCartCount) refreshCartCount();
      } else {
        message.error("Failed to remove from cart.");
      }
    } catch (error) {
      message.error("Failed to remove from cart.");
    }
  };

  const openDetailModal = (item) => {
    setDetailModal({ open: true, item, value: item.Detail || "" });
  };

  const handleDetailUpdate = async () => {
    const { item, value } = detailModal;
    if (!item) return;
    const payload = {
      ...item,
      Detail: value,
      LastUpdatedOn: new Date(),
    };
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/UserCart/UpdateSpecificationInCart",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Detail updated!");
        setCartData((prev) =>
          prev.map((cartItem) =>
            cartItem.ID === item.ID ? { ...cartItem, Detail: value } : cartItem
          )
        );
        setDetailModal({ open: false, item: null, value: "" });
      } else {
        message.error("Failed to update detail.");
      }
    } catch (error) {
      message.error("Failed to update detail.");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 72 }}>
        <Spin size="large" />
      </div>
    );
  }

  const totalBox = cartData.reduce((sum, item) => sum + (item.NoOfBox || 0), 0);
  const totalAmount = cartData.reduce(
    (sum, item) => sum + (item.Amount || 0),
    0
  );
  // Total Items by unique DesignNo
  const totalItems = new Set(cartData.map((item) => item.DesignNo)).size;

  return (
    <div className="cart-page">
      <div className="cart-header">
        <h1>Your Cart</h1>
        {cartData.length > 0 && (
          <p>
            {totalItems} item{totalItems !== 1 ? "s" : ""} · {totalBox} box
            {totalBox !== 1 ? "es" : ""}
          </p>
        )}
      </div>

      {cartData.length === 0 ? (
        <Empty
          description="Your cart is empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: "64px 0" }}
        >
          <Button type="primary" onClick={() => navigate("/")}>
            Browse Products
          </Button>
        </Empty>
      ) : (
        <div className="cart-layout">
          {/* Items */}
          <div className="cart-items">
            {cartData.map((item) => (
              <div className="cart-item" key={item.ID}>
                <img
                  className="cart-item__img"
                  src={item.ImageUrl}
                  alt={item.ProductName}
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMG;
                  }}
                />
                <div className="cart-item__body">
                  <div className="cart-item__row-top">
                    <div style={{ minWidth: 0 }}>
                      <h3 className="cart-item__name">{item.ProductName}</h3>
                      <p className="cart-item__meta">
                        Design No: {item.DesignNo}
                      </p>
                      <p className="cart-item__meta">
                        Color:{" "}
                        {item.Color && item.Color.trim()
                          ? item.Color
                          : "Mix Color"}{" "}
                        · Size: {item.Size}
                      </p>
                    </div>
                    <div className="cart-item__amount">
                      <span className="cart-item__total">
                        ₹{item.Amount.toFixed(2)}
                      </span>
                      <span className="cart-item__unit">
                        ₹{item.Price.toFixed(2)} / unit
                      </span>
                    </div>
                  </div>

                  <div className="cart-item__qty">
                    <span className="cart-item__qty-label">Boxes</span>
                    <Button
                      icon={<MinusOutlined />}
                      size="small"
                      onClick={() => handleQtyChange(item, item.NoOfBox - 1)}
                      disabled={item.NoOfBox <= 1 || updatingId === item.ID}
                    />
                    <InputNumber
                      min={1}
                      value={item.NoOfBox}
                      onChange={(value) => handleQtyChange(item, value)}
                      className="cart-item__qty-input"
                      disabled={updatingId === item.ID}
                    />
                    <Button
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => handleQtyChange(item, item.NoOfBox + 1)}
                      disabled={updatingId === item.ID}
                    />
                    <span className="cart-item__qty-calc">
                      × {item.BoxSize} = {item.TotalQuantity} pcs
                    </span>
                  </div>

                  {item.Detail && (
                    <div className="cart-item__detail">Note: {item.Detail}</div>
                  )}

                  <div className="cart-item__actions">
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      type="text"
                      onClick={() => openDetailModal(item)}
                    >
                      {item.Detail ? "Edit note" : "Add note"}
                    </Button>
                    <Popconfirm
                      title="Remove this item from cart?"
                      onConfirm={() => handleRemove(item.ID)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="small"
                        type="text"
                        danger
                        loading={updatingId === item.ID}
                      >
                        Remove
                      </Button>
                    </Popconfirm>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order summary */}
          <aside className="cart-summary">
            <h3 className="cart-summary__title">Order Summary</h3>
            <div className="cart-summary__row">
              <span>Total Items</span>
              <span>{totalItems}</span>
            </div>
            <div className="cart-summary__row">
              <span>Total Boxes</span>
              <span>{totalBox}</span>
            </div>
            <div className="cart-summary__divider" />
            <div className="cart-summary__row cart-summary__row--total">
              <span>Total Amount</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
            <Button
              type="primary"
              size="large"
              block
              className="cart-summary__btn"
              onClick={() => navigate("/cartoverview")}
              disabled={cartData.length === 0}
            >
              Place Order
            </Button>
          </aside>
        </div>
      )}

      <Modal
        title="Update Detail"
        open={detailModal.open}
        onOk={handleDetailUpdate}
        onCancel={() => setDetailModal({ open: false, item: null, value: "" })}
        okText="Update"
        cancelText="Cancel"
      >
        <Input.TextArea
          rows={3}
          value={detailModal.value}
          onChange={(e) =>
            setDetailModal((prev) => ({ ...prev, value: e.target.value }))
          }
          placeholder="Enter new detail"
        />
      </Modal>
    </div>
  );
};

export default Cart;
